import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

import { config } from '@/config';

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

export type EchoInstance = Echo<any>;

export function createEcho(bearerToken: string): EchoInstance {
  window.Pusher = Pusher;
  Pusher.logToConsole = true;

  console.log('[Echo] Creating instance', {
    host: config.reverb.host,
    port: config.reverb.port,
    scheme: config.reverb.scheme,
    hasToken: !!bearerToken,
  });

  const useTLS = config.reverb.scheme === 'https';

  // In development, Vite proxies /app → ws://localhost:8080 so the browser
  // connects on the same origin (port 3000) avoiding cross-origin WS issues.
  const isDev = import.meta.env.DEV;
  const wsHost = isDev ? window.location.hostname : config.reverb.host;
  const wsPort = isDev ? Number(window.location.port) || 3000 : config.reverb.port;

  console.log('[Echo] WebSocket target:', { wsHost, wsPort, useTLS, isDev });

  const echo = new Echo({
    broadcaster: 'reverb',
    key: config.reverb.key,
    wsHost,
    wsPort,
    wssPort: useTLS ? 443 : wsPort,
    forceTLS: useTLS,
    disableStats: true,
    cluster: '',
    enabledTransports: useTLS ? ['ws', 'wss'] : ['ws'],
    authEndpoint: `${config.api.base}/api/v2/broadcasting/auth`,
    auth: {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
    },
  });

  // Attach connection state logging
  try {
    const pusher = (echo as any).connector?.pusher;
    if (pusher?.connection) {
      console.log('[Echo] Initial connection state:', pusher.connection.state);
      ['connecting', 'connected', 'unavailable', 'failed', 'disconnected'].forEach((evt) => {
        pusher.connection.bind(evt, () => console.log(`[Echo] Connection → ${evt}`));
      });
      pusher.connection.bind('error', (err: any) => console.error('[Echo] Connection error:', err));
    }
  } catch (e) {
    console.warn('[Echo] Could not attach connection debug listeners:', e);
  }

  return echo;
}
